import React, { useRef } from 'react';
import { motion, useReducedMotion, useMotionValue, useSpring, useMotionTemplate, useAnimationControls } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { usePressBloom } from './PressBloom';
interface CreateShopButtonProps {
  onClick: () => void;
  size?: 'md' | 'lg';
  className?: string;
}
const EASE = [0.23, 1, 0.32, 1] as const;
/** Long enough to see the confirmation, short enough not to feel delayed. */
const HANDOFF_MS = 260;

/**
 * "Create my free AI shop" — the one conversion action on the page.
 *
 * Shares Ask AI's material (living drift, cursor light, light on press) but
 * sits on deep navy so it carries more weight. On press: a firm squeeze,
 * light blooms from your fingertip, a ring of light leaves the outline and
 * the arrow launches forward — then the next screen opens. The hand-off
 * waits a beat so that moment is actually seen instead of cut off.
 */
export function CreateShopButton({
  onClick,
  size = 'md',
  className = ''
}: CreateShopButtonProps) {
  const reduce = useReducedMotion();
  const {
    trigger,
    fillLayer,
    ringLayer
  } = usePressBloom('rounded-xl');
  const arrow = useAnimationControls();
  const busy = useRef(false);
  const mx = useMotionValue(50);
  const my = useMotionValue(50);
  const sx = useSpring(mx, {
    stiffness: 260,
    damping: 28,
    mass: 0.5
  });
  const sy = useSpring(my, {
    stiffness: 260,
    damping: 28,
    mass: 0.5
  });
  const spotlight = useMotionTemplate`radial-gradient(120px circle at ${sx}% ${sy}%, rgba(124,58,237,0.95) 0%, rgba(99,102,241,0.55) 45%, rgba(99,102,241,0) 80%), radial-gradient(32px circle at ${sx}% ${sy}%, rgba(255,255,255,0.18), rgba(255,255,255,0) 100%)`;
  const place = (e: React.PointerEvent<HTMLButtonElement>, jump: boolean) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width * 100;
    const y = (e.clientY - r.top) / r.height * 100;
    if (jump) {
      mx.jump(x);
      my.jump(y);
      sx.jump(x);
      sy.jump(y);
    } else {
      mx.set(x);
      my.set(y);
    }
  };
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (busy.current) return;
    if (reduce) {
      onClick();
      return;
    }
    busy.current = true;
    trigger(e);
    // The arrow launches out to the right and returns from the left.
    arrow.start({
      x: 18,
      opacity: 0,
      transition: {
        duration: 0.16,
        ease: EASE
      }
    }).then(() => {
      arrow.set({
        x: -10,
        opacity: 0
      });
      return arrow.start({
        x: 0,
        opacity: 1,
        transition: {
          duration: 0.18,
          ease: EASE
        }
      });
    });
    window.setTimeout(() => {
      busy.current = false;
      onClick();
    }, HANDOFF_MS);
  };
  const pad = size === 'lg' ? 'px-6 py-3.5 text-sm' : 'px-5 py-3 text-[13px]';
  return <motion.button onPointerEnter={e => place(e, true)} onPointerMove={e => place(e, false)} onClick={handleClick} whileHover={reduce ? undefined : 'hover'} whileTap={reduce ? undefined : 'press'} variants={{
    press: {
      scale: 0.96
    }
  }} transition={{
    type: 'spring',
    stiffness: 600,
    damping: 30
  }} className={`group/cta relative isolate inline-flex items-center gap-2 ${pad} font-bold text-white rounded-xl flex-shrink-0 shadow-[0_4px_14px_-6px_rgba(15,23,42,0.5)] hover:shadow-[0_10px_24px_-8px_rgba(109,40,217,0.55)] transition-shadow duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-indigo/40 focus-visible:ring-offset-2 ${className}`} data-id="element-1997" data-mp="2y3pj3zefx50p">
      <span aria-hidden className="absolute inset-0 rounded-xl overflow-hidden bg-dark-navy" data-id="element-1998" data-mp="pt6x3ussz4e2">
        <span className="ask-ai-drift absolute inset-0 bg-gradient-to-r from-dark-navy via-primary-indigo/70 to-dark-navy bg-[length:300%_100%]" data-id="element-1999" data-mp="usjew14thl9s" />
        <motion.span className="absolute inset-0 opacity-0 group-hover/cta:opacity-100 transition-opacity duration-300 ease-out" style={{
        backgroundImage: spotlight
      }} data-id="element-2000" data-mp="ytv8siuq4sns" />
        {/* While held down the fill brightens toward purple. */}
        <motion.span className="absolute inset-0 bg-ai-purple" initial={{
        opacity: 0
      }} variants={{
        press: {
          opacity: 0.25
        }
      }} transition={{
        duration: 0.1
      }} data-id="element-2001" data-mp="25atfl23jzj20" />
        {fillLayer}
        <span className="absolute inset-x-0 top-0 h-px bg-white/20" data-id="element-2002" data-mp="2f998nam552e5" />
      </span>

      {ringLayer}

      <span className="relative z-10" data-id="element-2003" data-mp="2abqv16x6hyzt">Create my free AI shop</span>
      <motion.span className="relative z-10 flex" variants={{
      hover: {
        x: 3
      }
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-2004" data-mp="1mmeqwlgpkh4d">
        <motion.span className="flex" animate={arrow} data-id="element-2005" data-mp="1wb6d0cz3877d">
          <ArrowRightIcon className="w-4 h-4" data-id="element-2006" data-mp="1wby9dq0ie8yp" />
        </motion.span>
      </motion.span>
    </motion.button>;
}